import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { ownerScopedKey, resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import {
  type ConceptCopyPointer,
  type ConceptCopySelectionResult,
  conceptCopyVariantService,
} from '@/services/data/ConceptCopyVariantService';

/**
 * Persists which `ad_units` row is the Concept's CURRENT copy —
 * `select_brief_concept_copy_revision`, the write §3 of the growth-intelligence
 * build spec names as the single unwritten column the whole paid-lineage chain
 * (and organic publish-from-concept, `PostService.createPostFromConcept`) is
 * blocked on. Deliberately a SEPARATE hook from `useConceptCopyVariants`
 * (spec's own suggestion) rather than folded into it: that hook's `variantsKey`
 * reads `ad_units`, this one also needs `project_tasks.version` /
 * `.current_ad_unit_id` — a second, smaller read this hook owns end to end.
 *
 * ── WHY A FRESH READ ON EVERY WRITE, NOT THE CACHED POINTER ─────────────────
 * `select_brief_concept_copy_revision` is optimistic-concurrency: it compares
 * the caller's `expectedConceptVersion` / `expectedCurrentAdUnitId` against the
 * live row and returns `{kind:'blocked'}` on a mismatch rather than clobbering.
 * A `useQuery`-cached pointer can be arbitrarily stale by the time the user
 * clicks (another tab, another reviewer, a generation that just landed), so
 * `selectConceptCopy` always re-fetches the pointer immediately before calling
 * the RPC. `fetchQuery` HONOURS staleTime and will happily return cached data,
 * and this app sets a global default of 5 minutes in src/config/queryClient.ts
 * — so `staleTime: 0` is passed EXPLICITLY below to force a real network read.
 * An earlier version of this comment claimed the default was already 0; it is
 * not, and that would have silently reintroduced exactly the stale-pointer race
 * this paragraph exists to prevent.
 * Passing a cached-but-stale pointer would not be a bug the RPC misses — it
 * would just make `blocked` fire more than necessary, silently degrading the
 * feature back toward "selection never sticks."
 */
export function useSelectConceptCopy(
  conceptId: string | null | undefined,
  briefId: string | null | undefined,
) {
  const { user } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const queryClient = useQueryClient();

  const { data: ownerId } = useQuery({
    queryKey: ['account-owner-id', user?.id],
    queryFn: () => resolveOwnerId(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });

  const brandKitId = activeKit?.id ?? null;
  const enabled = Boolean(ownerId && conceptId && brandKitId && !isActiveKitPending);
  const pointerKey = ownerScopedKey(
    ['concept-copy-pointer', brandKitId ?? 'no-workspace', conceptId ?? 'none'],
    ownerId,
  );
  const variantsKey = ownerScopedKey(
    ['concept-copy-variants', brandKitId ?? 'no-workspace', conceptId ?? 'none'],
    ownerId,
  );

  const pointerQuery = useQuery({
    queryKey: pointerKey,
    enabled,
    queryFn: () => conceptCopyVariantService.getConceptPointer(conceptId!, ownerId!, brandKitId!),
  });

  const mutation = useMutation({
    mutationFn: async (adUnitId: string): Promise<ConceptCopySelectionResult> => {
      if (!conceptId) throw new Error('No concept is open.');
      if (!briefId) throw new Error('This copy option has no Brief to select it against.');
      if (!ownerId) throw new Error('You must be signed in to select copy.');
      if (!brandKitId) throw new Error('Select a workspace before selecting copy.');

      const pointer = await queryClient.fetchQuery<ConceptCopyPointer | null>({
        queryKey: pointerKey,
        queryFn: () => conceptCopyVariantService.getConceptPointer(conceptId, ownerId, brandKitId),
        // Load-bearing: without this the app-wide 5-minute default applies and
        // the concurrency check compares against a pointer that may be minutes old.
        staleTime: 0,
      });
      if (!pointer) throw new Error('This concept could not be found.');

      return conceptCopyVariantService.select({
        briefId,
        conceptId,
        adUnitId,
        expectedConceptVersion: pointer.version,
        expectedCurrentAdUnitId: pointer.currentAdUnitId,
      });
    },
    onSuccess: (result) => {
      if (result.kind === 'blocked') {
        // The read this write compared against was stale (or the candidate
        // itself is no longer eligible) — re-read rather than leave the UI
        // showing a pointer/variant list the server just refused to trust.
        void queryClient.invalidateQueries({ queryKey: pointerKey });
        void queryClient.invalidateQueries({ queryKey: variantsKey });
        return;
      }
      queryClient.setQueryData<ConceptCopyPointer>(pointerKey, {
        version: result.conceptVersion,
        currentAdUnitId: result.currentAdUnitId,
      });
      void queryClient.invalidateQueries({ queryKey: variantsKey });
    },
  });

  return {
    /** `project_tasks.current_ad_unit_id` as last read — `null` before the first read resolves. */
    currentAdUnitId: pointerQuery.data?.currentAdUnitId ?? null,
    pointerLoading: pointerQuery.isPending,
    /**
     * A FAILED pointer read is not the same fact as "this Concept has no
     * current copy", and callers must not collapse the two. Both arrive here as
     * `currentAdUnitId === null`, so without this flag a network failure renders
     * as a confident claim of absence — the "missing is never zero" law, in the
     * one place where being wrong sends someone to create an ad with no lineage.
     */
    pointerFailed: pointerQuery.isError,
    /** Lets a caller offer a retry instead of a wrong claim about absence. */
    refetchPointer: pointerQuery.refetch,
    selectConceptCopy: mutation.mutateAsync,
    isSelecting: mutation.isPending,
  };
}
